import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { TestReport, TestSuite } from '../models/test-result.model';
import { TestEngineService } from './test-engine.service';

@Injectable({
    providedIn: 'root'
})
export class TestRunnerService {
    private testPopup: Window | null = null;
    private testResults$ = new Subject<TestReport>();
    private testProgress$ = new Subject<{ route: string; progress: number; status: string }>();

    constructor(private testEngine: TestEngineService) {}

    getTestResults(): Observable<TestReport> {
        return this.testResults$.asObservable();
    }

    getTestProgress(): Observable<{ route: string; progress: number; status: string }> {
        return this.testProgress$.asObservable();
    }

    async runTestsForRoute(routePath: string, routeName: string, rfqParams?: any): Promise<void> {
        try {
            this.testProgress$.next({ route: routeName, progress: 0, status: 'Starting...' });

            // Open popup window for testing
            const baseUrl = window.location.origin;
            
            // For Quote Assist with RFQ params, open directly at assist URL
            let testUrl: string;
            if ((routePath === 'assist' && routeName === 'Quote Assist' && rfqParams) ||
                (routePath === 'direct-assist' && routeName === 'Direct Quote Assist' && rfqParams)) {
                const queryParams = new URLSearchParams({
                    rfqId: rfqParams.rfqId,
                    companyId: rfqParams.companyId,
                    rfqPartId: rfqParams.rfqPartId,
                    contactId: rfqParams.contactId,
                    integration: 'test',
                    isTrackingEnabled: rfqParams.isTrackingEnabled,
                    showHeader: '1'
                });
                testUrl = `${baseUrl}/${routePath}?${queryParams.toString()}`;
                console.log(`🔧 ${routeName} test: Opening directly at ${routePath} URL with RFQ params`);
            } else {
                testUrl = `${baseUrl}/${routePath}`;
            }
            
            this.testPopup = window.open(
                testUrl,
                'testWindow',
                'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,menubar=no'
            );

            if (!this.testPopup) {
                throw new Error('Failed to open test popup window. Please allow popups for this site.');
            }

            // Wait for popup to load
            await this.waitForPopupLoad();
            
            this.testProgress$.next({ route: routeName, progress: 25, status: 'Running tests...' });

            // Run tests in the context of the popup
            const testSuite = await this.runTestsInPopup(routePath, rfqParams);
            
            this.testProgress$.next({ route: routeName, progress: 75, status: 'Generating report...' });

            // Create a test report with single suite
            const report: TestReport = {
                id: this.generateId(),
                name: `${routeName} Test Results`,
                startTime: testSuite.startTime!,
                endTime: testSuite.endTime,
                duration: testSuite.duration,
                testSuites: [testSuite],
                totalTests: testSuite.totalCount,
                passedTests: testSuite.successCount,
                failedTests: testSuite.failureCount,
                successRate: testSuite.totalCount > 0 ? (testSuite.successCount / testSuite.totalCount) * 100 : 0,
                environment: 'development'
            };

            this.testProgress$.next({ route: routeName, progress: 100, status: 'Complete!' });
            this.testResults$.next(report);

            // Close popup after a short delay
            setTimeout(() => this.closeTestPopup(), 2000);

        } catch (error) {
            this.testProgress$.next({ 
                route: routeName, 
                progress: 100, 
                status: `Error: ${error instanceof Error ? error.message : String(error)}` 
            });
            this.closeTestPopup();
            throw error;
        }
    }

    async runAllTests(): Promise<TestReport> {
        try {
            const report = await this.testEngine.runAllTests();
            this.testResults$.next(report);
            return report;
        } catch (error) {
            console.error('Failed to run all tests:', error);
            throw error;
        }
    }

    private async waitForPopupLoad(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.testPopup) {
                reject(new Error('Popup window not available'));
                return;
            }

            const checkLoad = () => {
                try {
                    if (this.testPopup!.document.readyState === 'complete') {
                        // Additional wait for Angular to bootstrap
                        setTimeout(resolve, 2000);
                    } else {
                        setTimeout(checkLoad, 100);
                    }
                } catch (e) {
                    // Cross-origin access might fail, wait a bit more
                    setTimeout(resolve, 3000);
                }
            };

            this.testPopup.addEventListener('load', () => {
                setTimeout(checkLoad, 1000);
            });

            // Fallback timeout
            setTimeout(() => {
                resolve();
            }, 10000);
        });
    }

    private async runTestsInPopup(routePath: string, rfqParams?: any): Promise<TestSuite> {
        if (!this.testPopup) {
            throw new Error('Test popup not available');
        }

        console.log(`🔧 Running tests in popup for route: ${routePath}`);
        
        // Create a special test engine context that operates on the popup window
        return await this.testEngine.runSingleRouteTestInWindow(routePath, this.testPopup, rfqParams);
    }

    private closeTestPopup(): void {
        if (this.testPopup && !this.testPopup.closed) {
            this.testPopup.close();
            this.testPopup = null;
        }
    }

    generateAndDownloadReport(report: TestReport): void {
        const htmlContent = this.testEngine.generateHtmlReport(report);
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `test-report-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    openReportInNewTab(report: TestReport): void {
        const htmlContent = this.testEngine.generateHtmlReport(report);
        const newWindow = window.open('', '_blank');
        if (newWindow) {
            newWindow.document.write(htmlContent);
            newWindow.document.close();
        }
    }

    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
} 